import { useEffect, useMemo, useRef, useState } from "react";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { useMsal } from "@azure/msal-react";
import { useTenant } from "../contexts/tenant-context";
import { MiniPackGenerationJobDto } from "../services/miniPackGenerationService";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const SIGNALR_SCOPE = process.env.REACT_APP_MSAL_API_SCOPE || "";

interface UseMiniPackGenerationSignalROptions {
  onJobUpdated: (job: MiniPackGenerationJobDto) => void;
  onConnectionError?: (message: string) => void;
}

export const useMiniPackGenerationSignalR = ({
  onJobUpdated,
  onConnectionError,
}: UseMiniPackGenerationSignalROptions) => {
  const { instance, accounts } = useMsal();
  const { selectedTenantId } = useTenant();

  const connectionRef = useRef<HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(HubConnectionState.Disconnected);

  const shouldConnect = useMemo(() => {
    return Boolean(
      selectedTenantId
      && selectedTenantId !== "__GLOBAL_ADMIN__"
      && API_BASE_URL
      && SIGNALR_SCOPE
    );
  }, [selectedTenantId]);

  useEffect(() => {
    if (!shouldConnect) {
      setConnectionState(HubConnectionState.Disconnected);
      return;
    }

    let isCancelled = false;

    const connect = async () => {
      const activeAccount = instance.getActiveAccount() ?? accounts[0];
      if (!activeAccount) {
        onConnectionError?.("Realtime updates are unavailable until authentication is ready.");
        return;
      }

      const baseUrl = API_BASE_URL.replace(/\/$/, "");
      const tenantQuery = encodeURIComponent(selectedTenantId!);
      const hubUrl = `${baseUrl}/hubs/mini-pack-generation-jobs?tenantId=${tenantQuery}`;

      const connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: async () => {
            const token = await instance.acquireTokenSilent({
              account: activeAccount,
              scopes: [SIGNALR_SCOPE],
            });

            return token.accessToken;
          },
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000])
        .configureLogging(LogLevel.Warning)
        .build();

      connection.on("miniPackJobUpdated", (job: MiniPackGenerationJobDto) => {
        onJobUpdated(job);
      });

      connection.onclose(() => {
        if (!isCancelled) {
          setConnectionState(HubConnectionState.Disconnected);
        }
      });

      connection.onreconnecting(() => {
        if (!isCancelled) {
          setConnectionState(HubConnectionState.Reconnecting);
        }
      });

      connection.onreconnected(() => {
        if (!isCancelled) {
          setConnectionState(HubConnectionState.Connected);
        }
      });

      try {
        await connection.start();
        if (!isCancelled) {
          connectionRef.current = connection;
          setConnectionState(HubConnectionState.Connected);
        } else {
          await connection.stop();
        }
      } catch (error) {
        onConnectionError?.(`Realtime connection failed: ${(error as Error).message}`);
        setConnectionState(HubConnectionState.Disconnected);
      }
    };

    connect();

    return () => {
      isCancelled = true;
      const current = connectionRef.current;
      connectionRef.current = null;
      if (current) {
        current.stop().catch(() => undefined);
      }
    };
  }, [accounts, instance, onConnectionError, onJobUpdated, selectedTenantId, shouldConnect]);

  return {
    isConnected: connectionState === HubConnectionState.Connected,
    connectionState,
  };
};
